/**
 * StripeOnboardingReturn.js
 *
 * Handles the return flow after users complete Stripe Connect onboarding.
 * Checks the onboarding status and redirects users appropriately.
 *
 * Route: /stripe/return
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { SERVER_URL } from './config';
import {
  getStripeOnboardingContext,
  clearStripeOnboardingContext,
} from './utils/stripeOnboardingHelper';

function StripeOnboardingReturn() {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [status, setStatus] = useState('checking'); // checking, success, incomplete, error
  const [message, setMessage] = useState('Verifying your Stripe account setup...');

  useEffect(() => {
    const checkStripeStatus = async (retryCount = 0) => {
      if (!user || !token) {
        setStatus('error');
        setMessage('Please log in to continue.');
        setTimeout(() => navigate('/login'), 5000);
        return;
      }

      try {
        console.log(`🔍 Checking Stripe status for user: ${user.id} (attempt ${retryCount + 1})`);

        // Check user's Stripe status
        const response = await fetch(`${SERVER_URL}/users/${user.id}/status-stripe`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('❌ Response error:', errorText);
          throw new Error(`Failed to check Stripe status: ${response.status}`);
        }

        const data = await response.json();
        console.log('📊 Stripe status data:', data);

        if (data.onboarding_completed && data.charges_enabled) {
          // Onboarding complete! Refresh user data to update AuthContext
          console.log('✅ Onboarding complete, refreshing user data...');
          await refreshUser();

          setStatus('success');

          // Check if there's a pending context (pool acceptance or gift claim)
          const onboardingContext = getStripeOnboardingContext();

          if (onboardingContext && onboardingContext.context === 'pool_acceptance') {
            setMessage('Your account is set up! Completing gift pool acceptance...');

            const { token: poolToken, beneficiaryName } = onboardingContext.data;

            // Complete the pool acceptance now that Stripe is set up
            try {
              const acceptRes = await fetch(`${SERVER_URL}/accept-pool/${poolToken}`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                  beneficiary_name: beneficiaryName,
                  beneficiary_email: user.email,
                  goal_amount: null,
                }),
              });

              if (acceptRes.ok) {
                // Clear the context
                clearStripeOnboardingContext();

                setMessage('Gift pool accepted successfully! Redirecting...');
                setTimeout(() => navigate('/feed?accepted=true', { replace: true }), 2000);
              } else {
                const errorData = await acceptRes.json();
                console.error('Failed to accept pool:', errorData);
                setMessage(
                  'Account setup complete, but failed to accept gift pool. Please try accepting again from your notifications.'
                );
                setTimeout(() => navigate('/feed', { replace: true }), 3000);
              }
            } catch (error) {
              console.error('Error accepting pool:', error);
              setMessage(
                'Account setup complete, but failed to accept gift pool. Please try accepting again from your notifications.'
              );
              setTimeout(() => navigate('/feed', { replace: true }), 3000);
            }
          } else {
            // No special context or different context - use default flow
            setMessage('Your account is set up! You can now receive gifts.');

            // Get the intended redirect destination
            const redirectTo = localStorage.getItem('post_stripe_redirect') || '/';
            localStorage.removeItem('post_stripe_redirect');

            setTimeout(() => navigate(redirectTo, { replace: true }), 3000);
          }
        } else if (data.has_stripe_account && !data.onboarding_completed) {
          // Account exists but onboarding not complete yet
          // Retry more times to give webhook time to fire (up to 10 seconds)
          if (retryCount < 5) {
            console.log(
              `⏳ Onboarding not complete yet, retrying in 2 seconds... (${retryCount + 1}/5)`
            );
            setMessage('Verifying your account setup...');
            setTimeout(() => checkStripeStatus(retryCount + 1), 2000);
          } else {
            setStatus('incomplete');
            setMessage(
              'Your Stripe setup is incomplete. Please complete the onboarding process to receive gifts.'
            );
          }
        } else if (!data.has_stripe_account) {
          // No account found - try to create one
          console.log('⚠️ No Stripe account found, attempting to create one...');
          setStatus('checking');
          setMessage('Setting up your Stripe account...');

          try {
            const setupResponse = await fetch(`${SERVER_URL}/users/${user.id}/setup-stripe`, {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (!setupResponse.ok) {
              throw new Error('Failed to create Stripe account');
            }

            const setupData = await setupResponse.json();

            if (setupData.onboarding_url) {
              console.log('✅ Created Stripe account, redirecting to onboarding...');
              window.location.href = setupData.onboarding_url;
            } else {
              throw new Error('No onboarding URL received');
            }
          } catch (setupError) {
            console.error('❌ Failed to create Stripe account:', setupError);
            setStatus('error');
            setMessage('Failed to set up Stripe account. Please try signing up again.');
          }
        }
      } catch (error) {
        console.error('❌ Error checking Stripe status:', error);
        // Retry on error too, in case it's a temporary issue or webhook delay
        if (retryCount < 5) {
          console.log(`⏳ Error occurred, retrying in 2 seconds... (${retryCount + 1}/5)`);
          // Keep status as 'checking' during retries to avoid flashing error screen
          setMessage('Verifying your account setup...');
          setTimeout(() => checkStripeStatus(retryCount + 1), 2000);
        } else {
          setStatus('error');
          setMessage(`Failed to verify your account setup: ${error.message}`);
        }
      }
    };

    // Add a small initial delay to give webhook time to fire
    setTimeout(() => checkStripeStatus(), 1000);
  }, [user, token, navigate]);

  const handleRetryOnboarding = async () => {
    if (!user || !token) return;

    try {
      setStatus('checking');
      setMessage('Generating new onboarding link...');

      const response = await fetch(`${SERVER_URL}/users/${user.id}/setup-stripe`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to generate onboarding link');
      }

      const data = await response.json();

      if (data.onboarding_url) {
        window.location.href = data.onboarding_url;
      } else {
        throw new Error('No onboarding URL received');
      }
    } catch (error) {
      console.error('Error retrying onboarding:', error);
      setStatus('error');
      setMessage('Failed to generate onboarding link. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-indigo-600 mb-8">💝 Gifted</h1>

          {status === 'checking' && (
            <div className="animate-pulse">
              <div className="text-6xl mb-4">⏳</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Checking Status</h2>
              <p className="text-gray-600">{message}</p>
            </div>
          )}

          {status === 'success' && (
            <div>
              <div className="text-6xl mb-4">✅</div>
              <h2 className="text-2xl font-bold text-green-600 mb-2">Setup Complete!</h2>
              <p className="text-gray-600">{message}</p>
            </div>
          )}

          {status === 'incomplete' && (
            <div>
              <div className="text-6xl mb-4">⚠️</div>
              <h2 className="text-2xl font-bold text-yellow-600 mb-2">Setup Incomplete</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <button
                onClick={handleRetryOnboarding}
                className="px-6 py-3 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
              >
                Complete Setup
              </button>
            </div>
          )}

          {status === 'error' && (
            <div>
              <div className="text-6xl mb-4">❌</div>
              <h2 className="text-2xl font-bold text-red-600 mb-2">Setup Error</h2>
              <p className="text-gray-600 mb-6">{message}</p>
              <div className="flex gap-4 justify-center">
                <button
                  onClick={handleRetryOnboarding}
                  className="px-6 py-3 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Go Home
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default StripeOnboardingReturn;
